import { useState } from 'react'

import ArrowDown2Icon from '../../../../../../../assets/svgs/ArrowDown2'
import CheckmarkIcon from '../../../../../../../assets/svgs/Checkmark'
import LiveIcon from '../../../../../../../assets/svgs/Live'

import { formatDateRange, joinParts, timeAgo, truncate } from '../../../../format'
import { setSprintRegistration } from '../../../../api'

import BoothDesk from './BoothDesk'
import style from './style.module.css'

/**
 * The company-controlled half of a booth.
 *
 * This is an AGGREGATOR, not a page. The modal's job is to show what is
 * happening at a company at a glance — so every block here is one or two lines
 * by default and everything else sits behind an expander. An earlier version
 * rendered the story, the anti-pitch, the hiring process, the benefits, three
 * highlights, two quotes and four Q&As all expanded, which turned a dashboard
 * into a scroll.
 *
 * Everything below the "Observed by Chimp" rule is scraped. Everything here is
 * written by the company, and stamped so a reader can tell which is which.
 */

const Expander = ({ label, count, children }) => {
  const [open, setOpen] = useState(false)
  return (
    <div className={style.exp}>
      <button
        type="button"
        className={open ? `${style.exp_btn} ${style.exp_btn_open}` : style.exp_btn}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-log-action="spotlight_expand_narrative"
      >
        <span>{label}</span>
        {count ? <span className={style.exp_count}>{count}</span> : null}
        <span className={style.exp_chev} aria-hidden="true"><ArrowDown2Icon /></span>
      </button>
      {open && <div className={style.exp_body}>{children}</div>}
    </div>
  )
}

const Stamp = () => (
  <span className={style.stamp}>
    <CheckmarkIcon />
    From the company
  </span>
)

/* --------------------------------------------------------------------------
   The sprint — the reason to open this booth this week, so it leads.
   Compact by default: the agenda and the assessment brief are one click away.
   -------------------------------------------------------------------------- */

const SprintBanner = ({ sprint, onAuthError }) => {
  const [status, setStatus] = useState(sprint.myStatus)
  const [count, setCount] = useState(sprint.registeredCount)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const registered = ['registered', 'waitlisted', 'attended'].includes(status)
  const spotsLeft = sprint.capacity === null ? null : Math.max(0, sprint.capacity - count)

  const toggle = async () => {
    setBusy(true)
    setError(null)
    const next = !registered
    try {
      const result = await setSprintRegistration({ sprintId: sprint.id, registered: next })
      setStatus(next ? result.status : null)
      setCount((n) => Math.max(0, next ? n + 1 : n - 1))
    } catch (err) {
      if (!(onAuthError && onAuthError(err))) setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  const facts = joinParts([
    formatDateRange(sprint.startsAt, sprint.endsAt),
    [sprint.venue, sprint.city].filter(Boolean).join(', ') || null,
    sprint.roles.length ? `${sprint.roles.length} roles` : null,
    spotsLeft !== null ? `${spotsLeft} places left` : null,
    sprint.cvRequired ? null : 'no CV',
  ])

  return (
    <section className={style.sprint}>
      <div className={style.sprint_head}>
        <span className={style.sprint_flag}>
          <LiveIcon />
          {sprint.format === '48h' ? '48h sprint' : 'Hiring sprint'}
        </span>
        {sprint.isDemo && <span className={style.demo}>Example</span>}
      </div>

      <h3 className={style.sprint_title}>{sprint.title}</h3>
      {sprint.promise && <p className={style.sprint_promise}>{sprint.promise}</p>}
      {facts && <p className={style.sprint_facts}>{facts}</p>}

      <div className={style.sprint_actions}>
        <button
          type="button"
          className={registered ? `${style.cta} ${style.cta_on}` : style.cta}
          onClick={toggle}
          disabled={busy || (!registered && !sprint.registrationOpen)}
          aria-pressed={registered}
          data-log-action="spotlight_sprint_register"
        >
          {busy
            ? 'Saving…'
            : registered
            ? status === 'waitlisted' ? 'On the waitlist' : "You're registered"
            : spotsLeft === 0
            ? 'Join waitlist'
            : 'Register'}
        </button>

        <span className={style.sprint_meta}>
          {sprint.registrationOpen && sprint.registrationClosesAt
            ? `closes ${timeAgo(sprint.registrationClosesAt)}`
            : sprint.registrationOpen
            ? `${count} registered`
            : 'registration closed'}
        </span>
      </div>

      {(sprint.agenda.length > 0 || sprint.assessmentSummary || sprint.roles.length > 0) && (
        <Expander label="How the two days work">
          {sprint.roles.length > 0 && (
            <p className={style.exp_line}>
              <strong>Roles</strong> {sprint.roles.join(' · ')}
            </p>
          )}
          {sprint.agenda.map((day, i) => (
            <p className={style.exp_line} key={day.day ?? i}>
              <strong>Day {day.day ?? i + 1}</strong> {day.title}
              {day.detail ? ` — ${day.detail}` : ''}
            </p>
          ))}
          {sprint.assessmentSummary && (
            <p className={style.exp_line}>
              <strong>What day one tests</strong> {sprint.assessmentSummary}
            </p>
          )}
        </Expander>
      )}

      {error && <p className={style.error} role="alert">{error}</p>}
    </section>
  )
}

/* -------------------------------------------------------------------------- */

const Narrative = ({ company, narrative, onAuthError }) => {
  const {
    profile,
    highlights = [],
    voices = [],
    sprints = [],
    questions = [],
    quiz = null,
    host = null,
    badges = [],
  } = narrative || {}
  const sprint = sprints.find((s) => s.registrationOpen) || sprints[0] || null

  const hasStory = profile && (profile.tagline || profile.story)

  // Unclaimed: this is the sales moment, not an empty state.
  if (!profile && !sprint) {
    return (
      <section className={style.unclaimed}>
        <span className={style.stamp_muted}>Unclaimed booth</span>
        <p className={style.unclaimed_lede}>
          Everything below is what Chimp observed about {company.name} on the public web — and none
          of it says why anyone should care. A claimed booth adds their story, their people, their
          real hiring process, and a 48-hour sprint that ends in an answer.
        </p>
      </section>
    )
  }

  return (
    <div className={style.band}>
      {profile && profile.isDemo && (
        <p className={style.demo_banner} role="note">
          <strong>Example booth.</strong> Nordvind Labs is fictional — written by Chimp to show what
          a company can publish here, not a real employer&apos;s words.
        </p>
      )}

      {sprint && <SprintBanner sprint={sprint} onAuthError={onAuthError} />}

      {/* The staffed desk and the fit check — the two things on this booth a
          careers page cannot do. They sit directly under the sprint because
          they are what someone does next. */}
      <BoothDesk
        company={company}
        host={host}
        quiz={quiz}
        badges={badges}
        sprintId={sprint ? sprint.id : null}
        onAuthError={onAuthError}
      />

      {/* The story, the anti-pitch, the hiring process, the highlights and the
          team quotes all live in one expander now. They are worth reading once
          and are not "what is happening", which is what this modal is for —
          two full panels of prose above the activity was the wrong weight. */}
      {(hasStory || voices.length > 0 || highlights.length > 0) && (
        <section className={style.panel}>
          <div className={style.panel_head}>
            <h3 className={style.panel_title}>About {company.name}</h3>
            <Stamp />
          </div>

          {profile && profile.tagline && <p className={style.tagline}>{profile.tagline}</p>}

          <Expander label="Their story, their team, how they hire">
            {profile && profile.story && (
              <p className={style.exp_line}>
                <strong>What they are building</strong> {profile.story}
              </p>
            )}
            {profile && profile.whyJoin && (
              <p className={style.exp_line}><strong>Why join</strong> {profile.whyJoin}</p>
            )}
            {profile && profile.honestNote && (
              <p className={`${style.exp_line} ${style.honest}`}>
                <strong>Who this is not for</strong> {profile.honestNote}
              </p>
            )}
            {profile && profile.hiringProcess && (
              <p className={style.exp_line}><strong>How they hire</strong> {profile.hiringProcess}</p>
            )}
            {profile && profile.benefits && profile.benefits.length > 0 && (
              <p className={style.exp_line}>
                <strong>Benefits</strong> {profile.benefits.join(' · ')}
              </p>
            )}
            {voices.map((v) => (
              <p className={style.exp_line} key={v.id}>
                <strong>{joinParts([v.name, v.role, v.tenure])}</strong>
                {truncate(v.quote, 220)}
              </p>
            ))}
            {highlights.map((h) => (
              <p className={style.exp_line} key={h.id}>
                <strong>{h.kind}</strong> {h.title}
                {h.detail ? ` - ${h.detail}` : ''}
              </p>
            ))}
          </Expander>
        </section>
      )}

      {/* Already-answered questions, as reference. Asking happens at the desk
          above, where it is addressed to a named person rather than to a form. */}
      {questions.length > 0 && (
        <section className={style.panel}>
          <div className={style.panel_head}>
            <h3 className={style.panel_title}>Already answered</h3>
            <Stamp />
          </div>

          <Expander label="Questions the team has answered" count={questions.length}>
            {questions.map((q) => (
              <p className={style.exp_line} key={q.id}>
                <strong>{q.question}</strong> {q.answer}
                {q.answeredByName ? ` — ${joinParts([q.answeredByName, q.answeredByRole])}` : ''}
              </p>
            ))}
          </Expander>
        </section>
      )}
    </div>
  )
}

export default Narrative
